/**
 * PublicReportGate — вход в отчёт по публичной ссылке.
 *
 * Сюда попадает человек без доступа в систему: агентство, инвестор,
 * подрядчик. Он пришёл по адресу, который ему прислали, и знает пароль —
 * или не знает.
 *
 * Экран сознательно ничего не рассказывает. Ни названия проекта, ни периода,
 * ни суммы под риском: всё это утекло бы всякому, кто увидел переписку с
 * пересланной ссылкой, ещё до ввода пароля. Заголовок называет только род
 * документа.
 *
 * Отозванная ссылка — отдельное состояние, а не молчание. Человеку, которому
 * отчёт уже показывали, важно понять, что ссылка перестала работать, а не
 * решить, что сломался сайт или он ошибся адресом.
 *
 * @format
 */

import {useId, useState, type FormEvent} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';

import {Button} from 'ui/components/atoms/Button';
import {Input} from 'ui/components/atoms/Input';
import {CenteredCard} from 'ui/components/composition/CenteredCard';
import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

export interface PublicReportGateProps {
  /**
   * Ссылка отозвана или отчёта больше нет.
   *
   * Поле ввода в этом состоянии не показываем: пароль подбирать не к чему,
   * и предлагать его ввести значит врать о том, что дверь ещё есть.
   */
  revoked?: boolean;
  /** Текст ошибки последней попытки. Пусто — ошибки не было. */
  error?: string | null;
  /** Проверка пароля в полёте. */
  busy?: boolean;
  onSubmit?: (password: string) => void;
}

const Title = styled(Box)({
  ...text('heading.m'),
  color: surfaceVar('onSurface'),
  margin: 0,
});

const Explain = styled(Box)({
  ...text('body.m'),
  color: surfaceVar('onSurfaceLight'),
});

const Page = styled(Box)(({theme}) => ({
  backgroundColor: theme.palette.background.default,
  color: surfaceVar('onSurface'),
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
}));

export function PublicReportGate({
  revoked = false,
  error,
  busy = false,
  onSubmit,
}: PublicReportGateProps) {
  const {t} = useTranslation();
  const [password, setPassword] = useState('');
  const fieldId = useId();

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const value = password.trim();
    if (!value || busy) return;
    onSubmit?.(value);
  };

  if (revoked) {
    return (
      <Page>
        <CenteredCard maxWidth='sm' radius='xl' padding='l'>
          <Stack gap='s'>
            <Title as='h1'>{t('Ссылка больше не действует', 'Ссылка больше не действует')}</Title>
            <Explain>
              {t(
                'Её удалил тот, кто её создал. Если отчёт всё ещё нужен, попросите прислать новую ссылку — у неё будет другой адрес и другой пароль.',
                'Её удалил тот, кто её создал. Если отчёт всё ещё нужен, попросите прислать новую ссылку — у неё будет другой адрес и другой пароль.',
              )}
            </Explain>
          </Stack>
        </CenteredCard>
      </Page>
    );
  }

  return (
    <Page>
      <CenteredCard
        maxWidth='sm'
        radius='xl'
        padding='l'
        cardAs='form'
        cardProps={{onSubmit: handleSubmit}}
      >
        <Stack gap='m'>
          <Stack gap='s'>
            <Title as='h1'>{t('Управленческий отчёт', 'Управленческий отчёт')}</Title>
            <Explain>
              {t(
                'Введите пароль, который прислали вместе со ссылкой.',
                'Введите пароль, который прислали вместе со ссылкой.',
              )}
            </Explain>
          </Stack>

          <Stack gap='xs'>
            <Input
              id={fieldId}
              type='password'
              placeholder={t('Пароль', 'Пароль')}
              value={password}
              onChange={event => setPassword(event.target.value)}
              autoFocus
              /*
               * `off` браузеры на `type=password` игнорируют, а гейт
               * отдаётся с того же origin, что и настоящий вход в кабинет:
               * менеджер паролей предложит сюда сохранённый пароль ОТ
               * АККАУНТА, и человек отправит его переменной мутации.
               * `new-password` для подавления они уважают.
               */
              autoComplete='new-password'
              /*
               * Ошибку отдаём самому `Input`, а не рисуем рядом: он и красит
               * поле, и ставит `aria-invalid`, и связывает сообщение через
               * `aria-describedby`. Своя разметка рядом выглядела так же, но
               * поле оставалось обычным, а связь терялась — `Input`
               * переобъявляет `aria-describedby` после `{...rest}`.
               */
              errorMessage={error ?? undefined}
            />
          </Stack>

          <Button type='submit' size='m' variant='solid' color='primary' disabled={busy}>
            {t('Открыть отчёт', 'Открыть отчёт')}
          </Button>
        </Stack>
      </CenteredCard>
    </Page>
  );
}
